/**
 * ProductsConfig
 * Unit Configurations management for product inventory.
 * Manages the unit_configurations table: add, edit, soft-delete.
 * Columns: Configuration, Structure, Units/Case, Cases/Pallet, Total/Pallet, Status, Actions.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../../../../supabaseClient';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, TextField, Grid, Paper,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, IconButton, Stack, useTheme, alpha, CircularProgress,
  FormControlLabel, Switch, TablePagination, InputAdornment, Alert, Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Inventory2 as PackageIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

const DEFAULT_FORM = {
  name: '',
  base_unit_name: 'units',
  enable_cases: true,
  case_unit_name: 'cases',
  units_per_case: 12,
  enable_pallets: true,
  pallet_unit_name: 'pallets',
  cases_per_pallet: 42
};

function getConfigDescription(config) {
  if (!config.enable_cases && !config.enable_pallets) return `${config.base_unit_name} only`;
  if (config.enable_cases && !config.enable_pallets) return `${config.base_unit_name} → ${config.case_unit_name}`;
  return `${config.base_unit_name} → ${config.case_unit_name} → ${config.pallet_unit_name}`;
}

function getTotalUnitsPerPallet(config) {
  if (config.enable_pallets && config.enable_cases) return config.units_per_case * config.cases_per_pallet;
  if (config.enable_cases) return config.units_per_case;
  return 1;
}

const fieldSx = (theme) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.9)',
    '& fieldset': { borderColor: alpha(theme.palette.primary.main, 0.3), borderWidth: 1 },
    '&:hover fieldset': { borderColor: theme.palette.primary.main },
    '&.Mui-focused fieldset': { borderColor: theme.palette.primary.main }
  }
});

export default function ProductsConfig({ companyId, canEdit = true, canDelete = true }) {
  const theme = useTheme();
  const [configs, setConfigs]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [searchTerm, setSearchTerm]     = useState('');
  const [page, setPage]                 = useState(0);
  const [rowsPerPage, setRowsPerPage]   = useState(10);
  const [showDialog, setShowDialog]     = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [formData, setFormData]         = useState(DEFAULT_FORM);
  const [formError, setFormError]       = useState(null);
  const [saving, setSaving]             = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, config: null });
  const [snackbar, setSnackbar]         = useState({ open: false, message: '', severity: 'success' });

  const loadConfigs = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('unit_configurations')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      const sorted = (data || []).sort((a, b) => {
        const diff = getTotalUnitsPerPallet(b) - getTotalUnitsPerPallet(a);
        return diff !== 0 ? diff : a.name.localeCompare(b.name);
      });
      setConfigs(sorted);
    } catch (err) {
      console.error('Error loading unit configs:', err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);
  useEffect(() => { setPage(0); }, [searchTerm]);

  const filtered = useMemo(() => {
    if (!searchTerm) return configs;
    const q = searchTerm.toLowerCase();
    return configs.filter(c =>
      (c.name || '').toLowerCase().includes(q) ||
      (c.base_unit_name || '').toLowerCase().includes(q) ||
      (c.case_unit_name || '').toLowerCase().includes(q) ||
      (c.pallet_unit_name || '').toLowerCase().includes(q) ||
      getConfigDescription(c).toLowerCase().includes(q) ||
      String(c.units_per_case || '').includes(q) ||
      String(c.cases_per_pallet || '').includes(q)
    );
  }, [configs, searchTerm]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  const openAdd = () => {
    setEditingConfig(null);
    setFormData(DEFAULT_FORM);
    setFormError(null);
    setShowDialog(true);
  };

  const openEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      name:            config.name,
      base_unit_name:  config.base_unit_name  || 'units',
      enable_cases:    config.enable_cases,
      case_unit_name:  config.case_unit_name  || 'cases',
      units_per_case:  config.units_per_case  || 1,
      enable_pallets:  config.enable_pallets,
      pallet_unit_name: config.pallet_unit_name || 'pallets',
      cases_per_pallet: config.cases_per_pallet || 1
    });
    setFormError(null);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setEditingConfig(null);
    setFormData(DEFAULT_FORM);
    setFormError(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { setFormError('Configuration name is required.'); return; }
    if (formData.enable_cases && parseInt(formData.units_per_case) < 1) { setFormError('Units per case must be at least 1.'); return; }
    if (formData.enable_pallets && parseInt(formData.cases_per_pallet) < 1) { setFormError('Cases per pallet must be at least 1.'); return; }

    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        company_id:       companyId,
        name:             formData.name.trim(),
        base_unit_name:   formData.base_unit_name,
        enable_cases:     formData.enable_cases,
        case_unit_name:   formData.enable_cases  ? formData.case_unit_name  : null,
        units_per_case:   formData.enable_cases  ? parseInt(formData.units_per_case)  : 1,
        enable_pallets:   formData.enable_pallets,
        pallet_unit_name: formData.enable_pallets ? formData.pallet_unit_name : null,
        cases_per_pallet: formData.enable_pallets ? parseInt(formData.cases_per_pallet) : 1
      };

      if (editingConfig) {
        const { error } = await supabase.from('unit_configurations').update(payload).eq('id', editingConfig.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('unit_configurations').insert([payload]);
        if (error) throw error;
      }

      setSnackbar({ open: true, message: editingConfig ? 'Configuration updated.' : 'Configuration created.', severity: 'success' });
      closeDialog();
      loadConfigs();
    } catch (err) {
      console.error('Error saving config:', err);
      setFormError(err.message || 'Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const config = deleteDialog.config;
    if (!config) return;
    try {
      const { error } = await supabase.from('unit_configurations').update({ is_active: false }).eq('id', config.id);
      if (error) throw error;
      setSnackbar({ open: true, message: `Deleted "${config.name}".`, severity: 'success' });
      loadConfigs();
    } catch (err) {
      setSnackbar({ open: true, message: 'Failed to delete configuration.', severity: 'error' });
    } finally {
      setDeleteDialog({ open: false, config: null });
    }
  };

  const hSx = {
    fontWeight: 500, fontSize: '0.9rem', bgcolor: 'white',
    borderBottom: `2px solid ${theme.palette.primary.main}`,
    color: 'primary.main', py: 2, px: 3
  };

  return (
    <Box sx={{ pb: 4 }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        mb: 3, p: 3, borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: 'white',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        position: 'relative', overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        },
        '&::after': {
          content: '""', position: 'absolute', top: 0, right: 0,
          width: '200px', height: '200px',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          borderRadius: '50%', transform: 'translate(50%, -50%)', zIndex: 0
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 500, fontSize: '1.8rem', mb: 1, lineHeight: 1.2 }}>
            Product Unit Configurations
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.9rem', mb: 2.5, maxWidth: '400px' }}>
            Define standard unit sizes for products — cases, pallets, or units only.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Chip label={`${configs.length} Configurations`} size="small"
              sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }} />
            <Chip label={`${configs.filter(c => c.enable_cases).length} With Cases`} size="small"
              sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }} />
            <Chip label={`${configs.filter(c => c.enable_pallets).length} With Pallets`} size="small"
              sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: 'warning.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}` }} />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <IconButton onClick={loadConfigs}
            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, color: 'primary.main' }}>
            <RefreshIcon />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}
            disabled={!canEdit}
            sx={{
              borderRadius: 2, px: 3, py: 1.2, fontSize: '0.9rem', fontWeight: 500,
              boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}` },
              '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500', boxShadow: 'none' }
            }}>
            Add Configuration
          </Button>
        </Box>
      </Box>

      {loading && configs.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress size={48} />
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>Loading unit configurations...</Typography>
        </Box>
      ) : configs.length === 0 ? (
        <Paper sx={{ textAlign: 'center', py: 8, px: 4, border: '2px dashed', borderColor: 'divider', borderRadius: 2 }}>
          <Box sx={{ p: 3, borderRadius: '50%', bgcolor: alpha(theme.palette.grey[400], 0.1), border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, mb: 2, width: 100, height: 100, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <SettingsIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 500 }}>No Unit Configurations Found</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto', fontWeight: 500 }}>
            Create standard unit configurations that can be applied to products for flexible inventory management.
          </Typography>
          <Button variant="contained" size="large" startIcon={<AddIcon />} onClick={openAdd}
            disabled={!canEdit}
            sx={{ borderRadius: 3, px: 4, py: 1.5, fontWeight: 500, boxShadow: `0 6px 18px ${alpha(theme.palette.primary.main, 0.25)}`, '&.Mui-disabled': { bgcolor: 'grey.300', color: 'grey.500', boxShadow: 'none' } }}>
            Create First Configuration
          </Button>
        </Paper>
      ) : (
        <Box>
          {/* Search */}
          <Paper sx={{
            p: 3, mb: 3, borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`
          }}>
            <TextField
              fullWidth
              placeholder="Search unit configurations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: 'primary.main' }} /></InputAdornment>,
                sx: {
                  borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: 'white', height: 56,
                  '&:hover': { border: `1px solid ${theme.palette.primary.main}` },
                  '&.Mui-focused': { border: `1px solid ${theme.palette.primary.main}` }
                }
              }}
              sx={{
                '& .MuiInputBase-input': { fontSize: '1rem', fontWeight: 500, py: 2, px: 2 },
                '& .MuiOutlinedInput-root': { height: 56, '& fieldset': { border: 'none' }, '&:hover fieldset': { border: 'none' }, '&.Mui-focused fieldset': { border: 'none' } }
              }}
            />
          </Paper>

          {/* Table */}
          <Paper sx={{
            borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
            boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden',
            '&:hover': { boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`, border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}` }
          }}>
            <TableContainer sx={{ overflowX: 'hidden' }}>
              <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...hSx, minWidth: 200 }}>Configuration</TableCell>
                    <TableCell align="center" sx={hSx}>Structure</TableCell>
                    <TableCell align="center" sx={hSx}>Units/Case</TableCell>
                    <TableCell align="center" sx={hSx}>Cases/Pallet</TableCell>
                    <TableCell align="center" sx={hSx}>Total/Pallet</TableCell>
                    <TableCell align="center" sx={hSx}>Status</TableCell>
                    <TableCell align="center" sx={{ ...hSx, width: 120 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.map((config) => (
                    <TableRow key={config.id} hover sx={{
                      cursor: 'default', transition: 'all 0.2s ease', bgcolor: 'white',
                      '&:hover': { bgcolor: 'grey.50', transform: 'translateX(4px)', boxShadow: 'inset 4px 0 0 black' },
                      '& .MuiTableCell-root': { borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`, py: 3, px: 3 }
                    }}>
                      <TableCell>
                        <Typography variant="h6" sx={{ fontWeight: 400, fontSize: '1.1rem', mb: 0.5 }}>{config.name}</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>{getConfigDescription(config)}</Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                          {config.enable_cases && (
                            <Chip label="Cases" size="medium" sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.main', border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`, fontWeight: 400, fontSize: '0.85rem', height: 28 }} />
                          )}
                          {config.enable_pallets && (
                            <Chip label="Pallets" size="medium" sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main', border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`, fontWeight: 400, fontSize: '0.85rem', height: 28 }} />
                          )}
                          {!config.enable_cases && !config.enable_pallets && (
                            <Chip label="Units Only" size="medium" sx={{ bgcolor: alpha(theme.palette.grey[500], 0.1), color: 'grey.700', border: `1px solid ${alpha(theme.palette.grey[500], 0.2)}`, fontWeight: 400, fontSize: '0.85rem', height: 28 }} />
                          )}
                        </Box>
                      </TableCell>

                      <TableCell align="center">
                        <Typography variant="h6" sx={{ fontWeight: 400, fontSize: '1.05rem' }}>
                          {config.enable_cases ? config.units_per_case : 'N/A'}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography variant="h6" sx={{ fontWeight: 400, fontSize: '1.05rem' }}>
                          {config.enable_pallets ? config.cases_per_pallet : 'N/A'}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main', fontSize: '1.05rem' }}>
                          {config.enable_pallets && config.enable_cases
                            ? getTotalUnitsPerPallet(config).toLocaleString()
                            : config.enable_cases
                            ? `${config.units_per_case}/case`
                            : '1'}
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <Chip label="Active" color="success" size="medium" sx={{ fontWeight: 400, fontSize: '0.85rem', height: 32 }} />
                      </TableCell>

                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <IconButton size="medium" disabled={!canEdit} onClick={() => openEdit(config)} sx={{
                            color: 'primary.main', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, borderRadius: 2,
                            '&:hover': { bgcolor: 'primary.main', color: 'white', transform: 'scale(1.05)', boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` }
                          }}>
                            <EditIcon fontSize="medium" />
                          </IconButton>
                          <IconButton size="medium" disabled={!canDelete} onClick={() => setDeleteDialog({ open: true, config })} sx={{
                            color: 'error.main', border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`, borderRadius: 2,
                            '&:hover': { bgcolor: 'error.main', color: 'white', transform: 'scale(1.05)', boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}` }
                          }}>
                            <DeleteIcon fontSize="medium" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`, borderRadius: '0 0 12px 12px' }}>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filtered.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                sx={{ px: 3, py: 2 }}
              />
            </Box>
          </Paper>
        </Box>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={showDialog} onClose={closeDialog} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 3, background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`, boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`, overflow: 'hidden' } }}>
        <DialogTitle sx={{ p: 4, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`, display: 'flex', alignItems: 'center', gap: 3 }}>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, display: 'flex' }}>
            <SettingsIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            {editingConfig ? 'Edit Configuration' : 'Add Configuration'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          {formError && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setFormError(null)}>{formError}</Alert>
          )}
          <Grid container spacing={4}>
            {/* Basic Info */}
            <Grid item xs={12}>
              <Box sx={{ p: 3, borderRadius: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, background: 'rgba(255,255,255,0.9)', mt: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 500, mb: 3, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <SettingsIcon sx={{ fontSize: 28 }} /> Basic Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField fullWidth size="medium" label="Configuration Name" required
                      value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Standard Bottles, Large Cases, Individual Units"
                      sx={fieldSx(theme)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth size="medium" label="Base Unit Name"
                      value={formData.base_unit_name} onChange={(e) => setFormData({ ...formData, base_unit_name: e.target.value })}
                      placeholder="e.g., units, bottles, pieces"
                      sx={fieldSx(theme)} />
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Packaging Structure */}
            <Grid item xs={12}>
              <Box sx={{ p: 3, borderRadius: 2, border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`, background: 'rgba(255,255,255,0.9)' }}>
                <Typography variant="h5" sx={{ fontWeight: 500, mb: 3, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PackageIcon sx={{ fontSize: 28 }} /> Packaging Structure
                </Typography>

                {/* Cases */}
                <Grid container spacing={3} sx={{ mb: 3 }}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Switch checked={formData.enable_cases} color="primary"
                        onChange={(e) => setFormData({ ...formData, enable_cases: e.target.checked, enable_pallets: e.target.checked ? formData.enable_pallets : false })} />}
                      label={<Typography variant="h6" sx={{ fontWeight: 500 }}>Enable Cases</Typography>}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>Group individual units into cases for easier handling</Typography>
                  </Grid>
                  {formData.enable_cases && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth size="medium" label="Case Unit Name"
                          value={formData.case_unit_name} onChange={(e) => setFormData({ ...formData, case_unit_name: e.target.value })}
                          placeholder="e.g., cases, boxes, packs" sx={fieldSx(theme)} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth size="medium" label="Units per Case" type="number"
                          value={formData.units_per_case} onChange={(e) => setFormData({ ...formData, units_per_case: e.target.value })}
                          InputProps={{ inputProps: { min: 1 } }}
                          helperText="How many individual units make up one case"
                          sx={fieldSx(theme)} />
                      </Grid>
                    </>
                  )}
                </Grid>

                {/* Pallets */}
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={<Switch checked={formData.enable_pallets} color="primary" disabled={!formData.enable_cases}
                        onChange={(e) => setFormData({ ...formData, enable_pallets: e.target.checked })} />}
                      label={<Typography variant="h6" sx={{ fontWeight: 500 }}>Enable Pallets</Typography>}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                      Group cases into pallets for bulk storage {!formData.enable_cases && '(requires cases to be enabled)'}
                    </Typography>
                  </Grid>
                  {formData.enable_pallets && formData.enable_cases && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth size="medium" label="Pallet Unit Name"
                          value={formData.pallet_unit_name} onChange={(e) => setFormData({ ...formData, pallet_unit_name: e.target.value })}
                          placeholder="e.g., pallets, skids" sx={fieldSx(theme)} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth size="medium" label="Cases per Pallet" type="number"
                          value={formData.cases_per_pallet} onChange={(e) => setFormData({ ...formData, cases_per_pallet: e.target.value })}
                          InputProps={{ inputProps: { min: 1 } }}
                          helperText="How many cases fit on one pallet"
                          sx={fieldSx(theme)} />
                      </Grid>
                    </>
                  )}
                </Grid>

                {/* Quick Presets */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 2 }}>Quick Presets</Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <Button variant="outlined" size="medium" sx={{ borderRadius: 2, fontSize: '0.9rem', px: 3 }}
                      onClick={() => setFormData({ ...formData, enable_cases: false, enable_pallets: false })}>
                      Units Only
                    </Button>
                    <Button variant="outlined" size="medium" sx={{ borderRadius: 2, fontSize: '0.9rem', px: 3 }}
                      onClick={() => setFormData({ ...formData, enable_cases: true, enable_pallets: false, units_per_case: 12 })}>
                      Cases (12 units)
                    </Button>
                    <Button variant="outlined" size="medium" sx={{ borderRadius: 2, fontSize: '0.9rem', px: 3 }}
                      onClick={() => setFormData({ ...formData, enable_cases: true, enable_pallets: true, units_per_case: 12, cases_per_pallet: 42 })}>
                      Full (12×42)
                    </Button>
                    <Button variant="outlined" size="medium" sx={{ borderRadius: 2, fontSize: '0.9rem', px: 3 }}
                      onClick={() => setFormData({ ...formData, enable_cases: true, enable_pallets: true, units_per_case: 24, cases_per_pallet: 48 })}>
                      Large (24×48)
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, gap: 1 }}>
          <Button onClick={closeDialog} sx={{ borderRadius: 2, fontWeight: 500, color: 'text.secondary' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ borderRadius: 2, fontWeight: 500, px: 4, boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.3)}` }}>
            {saving ? 'Saving...' : editingConfig ? 'Save Changes' : 'Create Configuration'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, config: null })} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <DeleteIcon color="error" /> Confirm Delete
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography>
            Are you sure you want to delete <strong>"{deleteDialog.config?.name}"</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialog({ open: false, config: null })} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} sx={{ borderRadius: 2 }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
